import { PlusCircleIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Alert, AlertType } from "@/atoms-react/alert/index.react";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import { Base, InfoAlert, Label } from "@/atoms-react/text/Text.react";
import { useRefreshUser } from "@/features/auth/hooks/use-refresh-user";
import { role } from "@/features/auth/types";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import {
  Add_Company_Related_steps,
  AgentNatureMandate,
  CompanyRelatedPersonDtoRequest,
  FindOlkypassRelatedPersonCompanyType,
  LegalEntityLinkDto,
  OlkyPassProLinkType
} from "@/features/company/types";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import { useDocument } from "@/features/user/document/hooks/use-document";
import { Document } from "@/features/user/document/types";
import { useLinks } from "@/features/user/hooks/use-links";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useLocale } from "@/features/utils/hooks/use-locale";
import ConsentDocument, {
  DocumentAction
} from "@/molecules/consent-document/ConsentDocument.react";
import { OlkyPassCustomerStatus } from "@/molecules/olkypass-customer-status/label";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setIsRefetchTokenLoading } from "@/redux/slices/tokenSlice";
import { getUserRoles } from "@/redux/slices/userSlice";
import { LinkSchemas } from "@/types/legal-entity/links.type";
import { Themes } from "@/types/theme";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { LayoutTitle } from "@/views/_layout/index.react";
import { AddRepresentative as AddOrEditRepresentative } from "./AddRepresentative";
import { CompanyOrganizationalChart } from "./CompanyDocuments";
import { EditCompanyRelatedLinkForm as AddOrEditMandataryInformation } from "./EditCompanyRelatedLinkForm";
import { FindAndAddCompanyRelatedLinkForm } from "./FindAndAddCompanyRelatedLinkForm";
import { ListCompanyRepresentative } from "./ListCompanyRepresentative";
import { SendRepresentativeCreateOlkyPassInvitation } from "./SendRepresentativeCreateOlkyPassInvitation";
import {
  SendRepresentativeInvitation,
  SendRepresentativeModalInfos
} from "./SendRepresentativeInvitation";

export type RepresentativeItem = {
  uuid: string;
  name: string;
  mail: string | null;
  birthdate: string;
  type: OlkyPassProLinkType;

  agentNature: AgentNatureMandate | null;
  agentNatureOther: string | null;

  auditPending: boolean | null;
  olkypassLvl: number;
  olkypassRequiredLvl: number;
  olkypassAuditLvl: number | null;
  olkyPassCustomerStatus: OlkyPassCustomerStatus | null;
  LeAgentUpgradeOlkyPassAsk: Date | null;

  realIdentityUuid: string | null;
};

type CompanyRelatedPersonProps = {
  isOnBoarding?: boolean;
};

export const CompanyRelatedPerson = ({ isOnBoarding = false }: CompanyRelatedPersonProps) => {
  const { leUuid } = useCompanyContext();
  const userRoles = useAppSelector(getUserRoles);
  const isAdmin = (() => userRoles.includes(role.ADMIN))();
  const queryClient = useQueryClient();
  const {
    addRepresentativeDataMutation,
    deleteCompanyLinkMutation,
    sendEmailCompanyDataMutation,
    editRepresentativeDataMutation,
    companyDetailQuery,
    companyDetailQueryAdmin,
    completeCompanyDataMutation,
    fetchOlkypass,
    legalEntityConsentDocumentQuery,
    signLegalEntityConsentDocumentMutation,
    useGetLinkInfo
  } = useCompany();

  const { useGetLinksFromLegalEntity } = useLinks();
  const {
    data: linksQuery,
    refetch: refetchLinks,
    isPending: isPendingCompaniesQuery
  } = useGetLinksFromLegalEntity();

  const { refreshToken, isRefreshTokenLoading } = useRefreshUser();
  const { UploadDocumentMutation, DocumentsDetailsMutation, DocumentDownloadMutation } =
    useDocument();
  const { mutateAsync: uploadDocument } = UploadDocumentMutation();
  const { mutateAsync: getDocumentMutation } = DocumentsDetailsMutation();
  const { mutateAsync: downloadDocument } = DocumentDownloadMutation();
  const { allDocumentTypesQuery } = useKYC();
  const { data: allCategories, refetch: refetchDocType } = allDocumentTypesQuery({
    queryKey: ["documents", "types", "all"],
    enabled: false
  });

  const dispatch = useAppDispatch();
  const { mutateAsync: fetchOlkypassMutation, isPending: loadingFetchOlkypass } = fetchOlkypass();
  const { data: companyDetailsUser, isPending: isPendingDetails } = companyDetailQuery(leUuid);
  const { data: companyDetailsAdmin, isPending: isPendingAdminDetails } = companyDetailQueryAdmin(
    leUuid ?? null
  );
  const { mutateAsync: completeCompanyAdd, isPending: loadingCompanyComplete } =
    completeCompanyDataMutation();
  const {
    mutateAsync: signLegalEntityConsentDocument,
    isPending: isSignLegalEntityConsentDocumentPending
  } = signLegalEntityConsentDocumentMutation();
  const { mutateAsync: getLinkInfo } = useGetLinkInfo();
  const [isGetLinkInfoPending, setIsGetLinkInfoPending] = useState<boolean>(false);
  const [showConsentDocument, setShowConsentDocument] = useState<boolean>(false);
  const { mutateAsync: addAgentOrBo, isPending: isAddRepresentativeMutationPending } =
    addRepresentativeDataMutation();
  const { mutateAsync: deleteLink, isPending: isDeletePending } = deleteCompanyLinkMutation();
  const { mutateAsync: editLink, isPending: isUpdatePending } = editRepresentativeDataMutation();
  const { mutateAsync: sendInvitation, isPending: loadingSendInvite } =
    sendEmailCompanyDataMutation();
  const { locale } = useLocale();
  const [addAgent, setAddAgent] = useState<boolean>(false);
  const [infoAgentEditable, setInfoAgentEditable] = useState<boolean>(false);
  const [addBeneficiairy, setAddBeneficiairy] = useState<boolean>(false);
  const [editAgent, setEditAgent] = useState<boolean>(false);
  const [editAgentLoading, setEditAgentLoding] = useState<boolean>(false);

  const [editBeneficiairy, setEditBeneficiairy] = useState<boolean>(false);
  const [editData, setEditData] = useState<LegalEntityLinkDto>();

  const [realUuidLink, setRealUuidLink] = useState<string>();
  const [realUuid, setRealUuid] = useState<string>();
  const { checkCompanyInfoActionsPermission } = useUserCheckAccess();

  const {
    useUserProfile,
    useCSP,
    useActivities,
    useNatureMandate,
    useMandateService,
    useCountries
  } = useUser();

  const [modalInviteInfos, setModalInviteInfos] = useState<SendRepresentativeModalInfos | null>(
    null
  );
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const [modalInviteCreateOlkyPassInfos, setModalInviteCreateOlkyPassInfos] =
    useState<SendRepresentativeModalInfos | null>(null);
  const [stepsAdd, setStepsAdd] = useState<Add_Company_Related_steps>(
    Add_Company_Related_steps.ADD_0
  );
  const { data: countries } = useCountries(locale.language);
  const { data: user } = useUserProfile();
  const companyDetails = companyDetailsUser ?? companyDetailsAdmin;

  const {
    data: consentDocument,
    refetch: refetchConsentDocument,
    isPending: isPendingDoc
  } = legalEntityConsentDocumentQuery(!!companyDetails?.legalEntity?.UserValidation);
  const [representative, setRepresentative] = useState<RepresentativeItem | null>(null);
  const { data: CSPCodes } = useCSP(locale.language, {
    enabled:
      ((!isAdmin && !companyDetails?.legalEntity?.UserValidation) ||
        (isAdmin && !companyDetails?.legalEntity?.AgentReviewFinish)) &&
      (addAgent || addBeneficiairy || infoAgentEditable),
    queryKey: ["static-data", "CSP", locale.language]
  });
  const { data: activities } = useActivities(locale.language, {
    enabled:
      ((!isAdmin && !companyDetails?.legalEntity?.UserValidation) ||
        (isAdmin && !companyDetails?.legalEntity?.AgentReviewFinish)) &&
      (addAgent || addBeneficiairy || infoAgentEditable),
    queryKey: ["static-data", "activities", locale.language]
  });
  const { data: natureMandates, isPending: isPendingNatureMandates } = useNatureMandate(
    locale.language,
    (addAgent || addBeneficiairy || editAgent || editBeneficiairy) &&
      (isAdmin || !companyDetails?.legalEntity?.UserValidation)
  );

  const { data: mandateServices, isLoading } = useMandateService(
    locale.language,
    (isAdmin || !companyDetails?.legalEntity?.UserValidation) &&
      (addAgent || addBeneficiairy || editAgent || editBeneficiairy)
  );

  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const translation = useTranslation(["pass", "error"]);
  const { t } = translation;

  const { getLanguage } = usei18n();

  const actionEnabled = checkCompanyInfoActionsPermission();

  const getSelectedRepresentativeList = (
    type: OlkyPassProLinkType,
    linksOlkyPassProList: LinkSchemas[]
  ): RepresentativeItem[] => {
    const createRepresentativeItem = (link: LinkSchemas): RepresentativeItem => {
      const olkyPassStatus = link.olkyPass.level;
      const olkyPassStatusUnderAudit = link.olkyPass.levelUnderAudit;
      const isUnderAuditPending =
        olkyPassStatusUnderAudit > olkyPassStatus && olkyPassStatusUnderAudit !== 1;

      const birthDate = link.naturalPerson.birthDate
        ? getLanguage() === "fr"
          ? new Date(link.naturalPerson.birthDate).toLocaleDateString("fr-FR")
          : new Date(link.naturalPerson.birthDate).toLocaleDateString("en-GB")
        : "";

      return {
        uuid: link.uuid,
        name: `${link.naturalPerson.firstname} ${link.naturalPerson.lastname}`,
        mail: link.realIdentity.mail,
        birthdate: birthDate,
        type: link.type,
        auditPending: isUnderAuditPending,
        olkypassLvl: link.olkyPass.level,
        olkypassRequiredLvl: link.minimumOlkyPassLevel,
        olkypassAuditLvl: link.olkyPass.levelUnderAudit,
        LeAgentUpgradeOlkyPassAsk: link.agentUpgradeOlkypassAsk
          ? new Date(link.agentUpgradeOlkypassAsk)
          : null,
        agentNature: link.agentNature?.defaultName ?? null,
        agentNatureOther: link.agentNatureOther,
        olkyPassCustomerStatus: link.olkyPass.olkyPassCustomerStatus,
        realIdentityUuid: link.realIdentity.uuid
      };
    };

    const selectedLinksOlkyPassPro = linksOlkyPassProList.filter((link) => link.type === type);

    return selectedLinksOlkyPassPro.map((linkOlkyPassPro) =>
      createRepresentativeItem(linkOlkyPassPro)
    );
  };

  const agentsList = useMemo(
    () => getSelectedRepresentativeList(OlkyPassProLinkType.AGENT, linksQuery ?? []),
    [linksQuery]
  );

  const beneficialOwnersList = useMemo(
    () => getSelectedRepresentativeList(OlkyPassProLinkType.BENEFICIAL_OWNER, linksQuery ?? []),
    [linksQuery]
  );

  const onDeleteOpen = (data: RepresentativeItem) => {
    setRepresentative(data);
    setOpenDelete(true);
  };

  const handleEditAgentOrBO = async (companyRepresentative: LegalEntityLinkDto) => {
    if (!leUuid) return;
    try {
      setEditAgentLoding(true);
      if (
        companyRepresentative.naturalPerson?.identityJustification &&
        companyRepresentative.naturalPerson?.identityJustification.isDirty
      ) {
        await refetchDocType();
        const docType = allCategories?.find((doc) => doc.type_en === "ID card");
        const resUpload = await uploadDocument({
          ownerUUID: companyRepresentative.linkUuid ?? "",
          language: getLanguage(),
          document_type_id: docType?.id,
          document: {
            base64Doc: companyRepresentative.naturalPerson?.identityJustification?.base64docs,
            fileFormat: companyRepresentative.naturalPerson?.identityJustification
              ?.fileFormat as string,
            fileName: companyRepresentative.naturalPerson?.identityJustification?.name
          },
          isAgentLe: true
        });
        if (resUpload.session_id) {
          const resp = await editLink({
            companyRelatedPersonDto: {
              ...companyRepresentative,
              naturalPerson: {
                ...companyRepresentative.naturalPerson,
                taxAddress: companyRepresentative.naturalPerson.taxAddress?.countryUuid
                  ? companyRepresentative.naturalPerson.taxAddress
                  : undefined,
                identityJustificationUuid: resUpload.session_id,
                identityJustification: undefined
              }
            },
            companyUuid: leUuid
          });
          if (resp.success) {
            await refetchLinks();
            const editedLink = linksQuery?.find(
              (link) => link.uuid === companyRepresentative.linkUuid
            );
            if (editedLink?.realIdentity.uuid === user?.naturalPerson.UUID) {
              await refreshToken();
            }
          }
        }
      } else {
        const resp = await editLink({
          companyRelatedPersonDto: {
            ...companyRepresentative,
            naturalPerson: companyRepresentative.naturalPerson
              ? {
                  ...companyRepresentative.naturalPerson,
                  taxAddress: companyRepresentative.naturalPerson?.taxAddress?.countryUuid
                    ? companyRepresentative.naturalPerson.taxAddress
                    : undefined
                }
              : undefined
          },
          companyUuid: leUuid
        });
        if (resp.success) {
          await refetchLinks();
          const linkRealUuid = linksQuery?.find(
            (link) => link.uuid === companyRepresentative.linkUuid
          );
          if (linkRealUuid?.realIdentity.uuid === user?.naturalPerson.UUID) {
            await refreshToken();
          }
        }
      }
    } catch (_) {
      if (editAgent) setEditAgent(false);
      else setEditBeneficiairy(false);
      if (infoAgentEditable) setInfoAgentEditable(false);
      if (selectedDocument) setSelectedDocument(null);
      setEditData(undefined);
    } finally {
      setEditAgentLoding(false);
      setEditData(undefined);

      if (editAgent) setEditAgent(false);
      else setEditBeneficiairy(false);

      if (infoAgentEditable) setInfoAgentEditable(false);
      if (selectedDocument) setSelectedDocument(null);
      dispatch(setIsRefetchTokenLoading(false));
    }
  };

  const handleOpenDulicate = (link: RepresentativeItem) => {
    const linkFound = linksQuery?.find((item) => item.uuid === link.uuid);

    setAddBeneficiairy(true);
    setStepsAdd(Add_Company_Related_steps.ADD_1);

    if (linkFound) setRealUuidLink(linkFound.uuid);
  };

  const handleDocumentAction = async ({
    documentAction,
    comment
  }: {
    documentConsentUuid?: string;
    documentAction: DocumentAction;
    comment?: string;
  }) => {
    if (documentAction === "cancel") {
      setShowConsentDocument(false);
    } else {
      try {
        await signLegalEntityConsentDocument({
          status: documentAction,
          comment: comment ?? null
        });
        await refetchConsentDocument();
      } catch (_) {
        toast.error(t("business_links.consentdocument.sign.error"));
      } finally {
        setShowConsentDocument(false);
      }
    }
  };

  const handleSendInvite = async (email?: string) => {
    try {
      if (!leUuid) {
        toast.error(t("business_links.consentdocument.invite.error"));
        return;
      }
      const linkOlkyPassProUuid =
        modalInviteInfos?.link.uuid ?? modalInviteCreateOlkyPassInfos?.link.uuid;

      if (!linkOlkyPassProUuid) {
        toast.error(t("business_links.consentdocument.invite.error"));
        return;
      }
      await sendInvitation({
        companyUuid: leUuid,
        linkOlkyPassProUuid: linkOlkyPassProUuid,
        lang: getLanguage(),
        ...(email && { email: email })
      });
      await refetchLinks();
      toast.success(t("business_links.consentdocument.invite.sent"));
    } catch (e) {
      toast.error(extractErrorDescription(e, translation));
    }
    setModalInviteInfos(null);
    setModalInviteCreateOlkyPassInfos(null);
  };

  const hanleOpenEditModal = useCallback(
    async (data: RepresentativeItem) => {
      const link = linksQuery?.find((link) => link.uuid === data.uuid);
      if (link) {
        if ((link.olkyPass.level === 0 || isAdmin) && leUuid) {
          if (link.type === OlkyPassProLinkType.AGENT) setEditAgent(true);
          if (link.type === OlkyPassProLinkType.BENEFICIAL_OWNER) setEditBeneficiairy(true);

          try {
            setIsGetLinkInfoPending(true);
            const linkInfoData = await getLinkInfo({
              leUuid: leUuid,
              linkUuid: link.uuid
            });
            if (linkInfoData.naturalPerson?.identityJustificationUuid) {
              const listDocuments = await getDocumentMutation({
                UUID: link.uuid,
                isAgentLe: true
              });
              const doc = listDocuments.find(
                (document) =>
                  document.uuid === linkInfoData.naturalPerson?.identityJustificationUuid
              );
              if (doc) setSelectedDocument(doc);
            }
            setInfoAgentEditable(true);
            setEditData(linkInfoData);
            setIsGetLinkInfoPending(false);
          } catch (error) {
            toast.error(extractErrorDescription(error, translation));
            if (link.type === OlkyPassProLinkType.AGENT) setEditAgent(false);
            if (link.type === OlkyPassProLinkType.BENEFICIAL_OWNER) setEditBeneficiairy(false);
            setIsGetLinkInfoPending(false);
            if (selectedDocument) setSelectedDocument(null);

            if (infoAgentEditable) setInfoAgentEditable(false);
          }
        } else {
          setEditData({
            linkUuid: link.uuid,
            mandatoryInformation: {
              linkType: link.type,
              agentNatureRelation: link.agentNature?.defaultName,
              agentNatureRelationOther: link.agentNatureOther,
              boPercentage: link.beneficialOwnerPercentage,
              boRelationOther: link.beneficialOwnerNatureRelationOther,
              boRelations: link.beneficialOwnerRelations,
              agentCompanyName: link.agentCompanyName,
              isAgentLe: link.isAgentLe,
              isCommunicationContact: link.isCommunicationContact,
              mandateServices: link.mandateServices
            },
            realIdentityUuid: link.realIdentity.uuid
          });
          if (link.type === OlkyPassProLinkType.AGENT) setEditAgent(true);
          if (link.type === OlkyPassProLinkType.BENEFICIAL_OWNER) setEditBeneficiairy(true);
        }
      }
    },
    [linksQuery]
  );

  const handleCompanyAddComplete = async () => {
    const legalMandate = linksQuery?.find(
      (link) =>
        link.agentNature?.defaultName === AgentNatureMandate.LEGAL_REPRESENTATIVE ||
        link.agentNature?.defaultName === AgentNatureMandate.JUDICIAL_AGENT
    );
    if (!legalMandate) {
      toast.error(t("business_links.validation.error"));
      return;
    }
    if (!leUuid) return;

    try {
      await completeCompanyAdd(leUuid);
      toast.success(t("business_links.validation.success_complete"));
    } catch (_) {
      toast.error(t("business_links.validation.error_complete"));
    }
  };

  const isRepresentativeSelfDeleting = (
    representative: RepresentativeItem,
    linksQuery: LinkSchemas[]
  ) => {
    const filterResult = linksQuery.filter(
      (link) => link.realIdentity.uuid === representative.realIdentityUuid
    );
    if (filterResult.length === 1) return true;
    return false;
  };

  const isAddOrEditRepresentativePending =
    isAddRepresentativeMutationPending ||
    isRefreshTokenLoading ||
    isPendingCompaniesQuery ||
    isUpdatePending ||
    editAgentLoading;

  const isSendInvitePending = loadingSendInvite || isPendingCompaniesQuery;

  if (showConsentDocument && !!consentDocument) {
    return (
      <ConsentDocument
        padding={false}
        contentBase64={consentDocument}
        onDocumentAction={handleDocumentAction}
        isPendingConfirmDocument={isSignLegalEntityConsentDocumentPending}
        isPendingRejectDocument={isSignLegalEntityConsentDocumentPending}
        isModalConfirmationOpen={false}
      />
    );
  }

  if (!leUuid) return null;

  return (
    <div>
      {!isOnBoarding && <LayoutTitle>{t("business_links.title")}</LayoutTitle>}
      <div className="px-4">
        <>
          {openDelete && (
            <Modal
              open={openDelete}
              onClose={() => {
                setOpenDelete(false);
                setRepresentative(null);
              }}
              className="w-full max-w-2xl sm:w-full"
            >
              <ModalContent title={t("business_links.delete_link.title")}>
                <div className="mt-4 flex flex-col gap-4">
                  {representative &&
                    linksQuery &&
                    representative.realIdentityUuid === user?.naturalPerson.UUID &&
                    isRepresentativeSelfDeleting(representative, linksQuery) && (
                      <Label className="text-red-500">{t("companyLink.self_delete_message")}</Label>
                    )}
                  <Alert type={AlertType.Danger}>
                    <Base>{t("companyLink.confirm_delete", { name: representative?.name })}</Base>
                    <div className="mt-4 flex justify-end gap-4">
                      <Button
                        onClick={() => {
                          setOpenDelete(false);
                          setRepresentative(null);
                        }}
                        theme={Themes.white}
                        className="w-max"
                      >
                        {t("olkypass.general.cancel")}
                      </Button>
                      <Button
                        icon={<TrashIcon />}
                        loading={isDeletePending}
                        onClick={async () => {
                          try {
                            if (!representative) return;
                            const data = await deleteLink({
                              companyUuid: leUuid,
                              linkUuid: representative.uuid
                            });
                            if (data.success) {
                              const linkRealUuid = linksQuery?.find(
                                (link) => link.uuid === representative.uuid
                              );
                              if (linkRealUuid?.realIdentity.uuid === user?.naturalPerson.UUID) {
                                await refreshToken();
                              }

                              const data = await refetchLinks();
                              if (!data.data) {
                                queryClient.removeQueries({
                                  queryKey: ["companies", "representative", leUuid]
                                });
                              }

                              const successMessage =
                                representative.type === OlkyPassProLinkType.AGENT
                                  ? t("business_links.delete_link.agentDeleted")
                                  : t("business_links.delete_link.beneficiaryDeleted");

                              toast.success(successMessage);
                              setOpenDelete(false);

                              return;
                            } else {
                              toast.error(t("business_links.delete_link.error"));
                              setOpenDelete(false);
                            }
                          } catch (_) {
                            toast.error("Error");
                            setOpenDelete(false);
                          }
                        }}
                        className="w-max"
                        theme={Themes.danger}
                        disabled={!actionEnabled}
                      >
                        {t("olkypass.general.confirm")}
                      </Button>
                    </div>
                  </Alert>
                </div>
              </ModalContent>
            </Modal>
          )}

          {editAgent && (
            <Modal
              className="max-w-5xl"
              open={editAgent}
              onClose={() => {
                setEditAgent(false);
                setEditData(undefined);
                setRealUuidLink(undefined);
                setRealUuid(undefined);
                if (infoAgentEditable) setInfoAgentEditable(false);
                if (selectedDocument) setSelectedDocument(null);
              }}
            >
              {isGetLinkInfoPending ? (
                <div className="flex flex-row justify-center">
                  <Loader />
                </div>
              ) : infoAgentEditable ? (
                <AddOrEditRepresentative
                  agent={true}
                  loading={isAddOrEditRepresentativePending}
                  natureMandates={natureMandates}
                  isPendingNatureMandates={isPendingNatureMandates}
                  mandateServices={mandateServices}
                  CSPCodes={CSPCodes}
                  activitiesCsp={activities}
                  editableRepresentative={editData}
                  infoAgentEditable={infoAgentEditable}
                  countries={countries?.countries ?? []}
                  onValidate={handleEditAgentOrBO}
                  onDownload={downloadDocument}
                  documentUploaded={selectedDocument ?? undefined}
                />
              ) : (
                <AddOrEditMandataryInformation
                  isRepresentative={true}
                  isAddForm={false}
                  isLoadingNature={isLoading}
                  isPendingNatureMandates={isPendingNatureMandates}
                  onClose={() => {
                    setEditAgent(false);
                    setEditData(undefined);
                    setRealUuidLink(undefined);
                  }}
                  data={editData}
                  isUpdatePending={isUpdatePending}
                  natureMandates={natureMandates}
                  mandateServices={mandateServices}
                  onValidate={handleEditAgentOrBO}
                />
              )}
            </Modal>
          )}

          {editBeneficiairy && (
            <Modal
              className="max-w-5xl"
              open={editBeneficiairy}
              onClose={() => {
                setEditBeneficiairy(false);
                setEditData(undefined);
                setRealUuidLink(undefined);
                if (infoAgentEditable) setInfoAgentEditable(false);
                if (selectedDocument) setSelectedDocument(null);
              }}
            >
              {isGetLinkInfoPending ? (
                <div className="flex flex-row justify-center">
                  <Loader />
                </div>
              ) : infoAgentEditable ? (
                <AddOrEditRepresentative
                  agent={false}
                  loading={isAddOrEditRepresentativePending}
                  natureMandates={natureMandates}
                  isPendingNatureMandates={isPendingNatureMandates}
                  mandateServices={mandateServices}
                  CSPCodes={CSPCodes}
                  activitiesCsp={activities}
                  editableRepresentative={editData}
                  infoAgentEditable={infoAgentEditable}
                  countries={countries?.countries ?? []}
                  onValidate={handleEditAgentOrBO}
                  onDownload={downloadDocument}
                  documentUploaded={selectedDocument ?? undefined}
                />
              ) : (
                <AddOrEditMandataryInformation
                  isRepresentative={false}
                  isAddForm={false}
                  isLoadingNature={isLoading}
                  isPendingNatureMandates={isPendingNatureMandates}
                  onClose={() => {
                    setEditBeneficiairy(false);
                    setEditData(undefined);
                    setRealUuidLink(undefined);
                  }}
                  data={editData}
                  isUpdatePending={isUpdatePending}
                  natureMandates={natureMandates}
                  mandateServices={mandateServices}
                  onValidate={handleEditAgentOrBO}
                />
              )}
            </Modal>
          )}

          {addAgent && (
            <Modal
              className="max-w-5xl"
              open={addAgent}
              onClose={() => {
                setAddAgent(false);
                setStepsAdd(Add_Company_Related_steps.ADD_0);
                setEditData(undefined);
                setRealUuidLink(undefined);
              }}
            >
              {stepsAdd === Add_Company_Related_steps.ADD_0 && (
                <FindAndAddCompanyRelatedLinkForm
                  isRepresentative={true}
                  isUpdatePending={loadingFetchOlkypass}
                  onValidate={async (findIdPass: FindOlkypassRelatedPersonCompanyType) => {
                    if (!findIdPass.hasOlkypass)
                      return setStepsAdd(Add_Company_Related_steps.ADD_2);

                    const isAlreadyAdded = !!agentsList.find(
                      (bo) => bo.realIdentityUuid === findIdPass.idOlkypass
                    );

                    if (isAlreadyAdded) {
                      toast.error(t("olkypass.companyPage.notifications.error.agentAlreadyAdded"));
                      return;
                    }

                    fetchOlkypassMutation(findIdPass.idOlkypass as string)
                      .then((data) => {
                        if (data.success && findIdPass?.idOlkypass) {
                          setStepsAdd(Add_Company_Related_steps.ADD_1);
                          setRealUuid(findIdPass?.idOlkypass);
                        } else {
                          toast.error(t("business.error.error_findPass"));
                        }
                      })
                      .catch(() => toast.error(t("business.error.error_findPass")));
                  }}
                />
              )}
              {stepsAdd === Add_Company_Related_steps.ADD_1 && (
                // Add with uuid
                <AddOrEditMandataryInformation
                  isRepresentative={true}
                  isAddForm={true}
                  isLoadingNature={isLoading}
                  isUpdatePending={isAddOrEditRepresentativePending}
                  isPendingNatureMandates={isPendingNatureMandates}
                  natureMandates={natureMandates}
                  mandateServices={mandateServices}
                  onValidate={async (companyRepresentative: LegalEntityLinkDto) => {
                    try {
                      if (!realUuidLink && !realUuid) return;
                      dispatch(setIsRefetchTokenLoading(true));
                      companyRepresentative.mandatoryInformation.linkType =
                        OlkyPassProLinkType.AGENT;
                      const companyRepresentativeData: LegalEntityLinkDto = {
                        ...companyRepresentative
                      };
                      if (realUuidLink) companyRepresentativeData.linkUuid = realUuidLink;
                      if (realUuid) companyRepresentativeData.realIdentityUuid = realUuid;

                      const data: CompanyRelatedPersonDtoRequest = {
                        companyRelatedPersonDto: companyRepresentativeData,
                        companyUuid: leUuid
                      };

                      const resp = await addAgentOrBo(data);
                      if (resp.success) {
                        if (realUuid === user?.naturalPerson.UUID) {
                          await refreshToken();
                        }
                        await refetchLinks();
                        setAddAgent(false);
                        setRealUuid(undefined);
                        setRealUuidLink(undefined);
                      }
                    } catch (error) {
                      setAddAgent(false);
                      toast.error(extractErrorDescription(error, translation));
                    } finally {
                      dispatch(setIsRefetchTokenLoading(false));
                      setStepsAdd(Add_Company_Related_steps.ADD_0);
                    }
                  }}
                />
              )}
              {stepsAdd === Add_Company_Related_steps.ADD_2 && (
                <AddOrEditRepresentative
                  agent={true}
                  loading={isAddOrEditRepresentativePending}
                  natureMandates={natureMandates}
                  isPendingNatureMandates={isPendingNatureMandates}
                  mandateServices={mandateServices}
                  documentUploaded={selectedDocument ?? undefined}
                  CSPCodes={CSPCodes}
                  activitiesCsp={activities}
                  countries={countries?.countries ?? []}
                  onDownload={downloadDocument}
                  onValidate={async (companyRepresentative: LegalEntityLinkDto) => {
                    try {
                      setIsRefetchTokenLoading(true);
                      companyRepresentative.mandatoryInformation.linkType =
                        OlkyPassProLinkType.AGENT;
                      const data: CompanyRelatedPersonDtoRequest = {
                        companyRelatedPersonDto: companyRepresentative,
                        companyUuid: leUuid
                      };
                      const resp = await addAgentOrBo(data);
                      if (resp.success) {
                        if (
                          data.companyRelatedPersonDto.naturalPerson?.identityJustification?.name &&
                          data.companyRelatedPersonDto.naturalPerson?.identityJustification
                            .base64docs
                        ) {
                          try {
                            await refetchDocType();
                            const docType = allCategories?.find((doc) => doc.type_en === "ID card");
                            await uploadDocument({
                              ownerUUID: resp.data.linkUuid,
                              document_type_id: docType?.id,
                              isAgentLe: true,

                              language: getLanguage(),
                              document: {
                                base64Doc:
                                  data.companyRelatedPersonDto.naturalPerson?.identityJustification
                                    ?.base64docs,
                                fileFormat: data.companyRelatedPersonDto.naturalPerson
                                  ?.identityJustification?.fileFormat as string,
                                fileName:
                                  data.companyRelatedPersonDto.naturalPerson?.identityJustification
                                    ?.name
                              }
                            });
                            const linkRealUuid = linksQuery?.find(
                              (link) => link.uuid === companyRepresentative.linkUuid
                            );
                            if (linkRealUuid?.realIdentity.uuid === user?.naturalPerson.UUID) {
                              await refreshToken();
                            }
                            await refetchLinks();
                            setAddAgent(false);
                          } catch (_) {
                            toast.error(t("aliasDocumentsPage.error_import"));
                          }
                        } else {
                          const linkRealUuid = linksQuery?.find(
                            (link) => link.uuid === companyRepresentative.linkUuid
                          );
                          if (linkRealUuid?.realIdentity.uuid === user?.naturalPerson.UUID) {
                            await refreshToken();
                          }
                        }
                        await refetchLinks();
                        setAddAgent(false);
                        if (selectedDocument) setSelectedDocument(null);
                      }
                    } catch (error) {
                      setAddAgent(false);
                      toast.error(extractErrorDescription(error, translation));
                      if (selectedDocument) setSelectedDocument(null);
                    } finally {
                      setIsRefetchTokenLoading(false);
                      setStepsAdd(Add_Company_Related_steps.ADD_0);
                    }
                  }}
                />
              )}
            </Modal>
          )}

          {addBeneficiairy && (
            <Modal
              className="max-w-5xl"
              open={addBeneficiairy}
              onClose={() => {
                setAddBeneficiairy(false);
                setStepsAdd(Add_Company_Related_steps.ADD_0);
                setEditData(undefined);
                setRealUuidLink(undefined);
              }}
            >
              {stepsAdd === Add_Company_Related_steps.ADD_0 && (
                <FindAndAddCompanyRelatedLinkForm
                  isRepresentative={false}
                  isUpdatePending={loadingFetchOlkypass}
                  onValidate={async (findIdPass: FindOlkypassRelatedPersonCompanyType) => {
                    if (!findIdPass.hasOlkypass)
                      return setStepsAdd(Add_Company_Related_steps.ADD_2);
                    if (!findIdPass.idOlkypass) return;

                    const isAlreadyAdded = !!beneficialOwnersList.find(
                      (bo) => bo.realIdentityUuid === findIdPass.idOlkypass
                    );

                    if (isAlreadyAdded) {
                      toast.error(
                        t("olkypass.companyPage.notifications.error.beneficiairyAlreadyAdded")
                      );
                      return;
                    }

                    fetchOlkypassMutation(findIdPass.idOlkypass)
                      .then((data) => {
                        if (data.success && findIdPass?.idOlkypass) {
                          setStepsAdd(Add_Company_Related_steps.ADD_1);
                          setRealUuid(findIdPass?.idOlkypass);
                        } else {
                          toast.error(t("business.error.error_findPass"));
                        }
                      })
                      .catch(() => toast.error(t("business.error.error_findPass")));
                  }}
                />
              )}
              {stepsAdd === Add_Company_Related_steps.ADD_1 && (
                <AddOrEditMandataryInformation
                  isRepresentative={false}
                  isUpdatePending={isAddRepresentativeMutationPending}
                  isLoadingNature={isLoading}
                  isPendingNatureMandates={isPendingNatureMandates}
                  isAddForm={true}
                  natureMandates={natureMandates}
                  mandateServices={mandateServices}
                  data={editData}
                  onValidate={async (companyRepresentative: LegalEntityLinkDto) => {
                    if (!realUuidLink && !realUuid) return;
                    try {
                      dispatch(setIsRefetchTokenLoading(true));
                      companyRepresentative.mandatoryInformation.linkType =
                        OlkyPassProLinkType.BENEFICIAL_OWNER;
                      const companyRepresentativeData: LegalEntityLinkDto = {
                        ...companyRepresentative
                      };

                      if (realUuidLink) companyRepresentativeData.linkUuid = realUuidLink;
                      if (realUuid) companyRepresentativeData.realIdentityUuid = realUuid;

                      const data: CompanyRelatedPersonDtoRequest = {
                        companyRelatedPersonDto: companyRepresentativeData,
                        companyUuid: leUuid
                      };
                      const resp = await addAgentOrBo(data);
                      if (resp.success) {
                        if (realUuid === user?.naturalPerson.UUID) {
                          await refreshToken();
                        }
                        await refetchLinks();
                        setAddBeneficiairy(false);
                      }
                    } catch (error) {
                      setAddBeneficiairy(false);
                      toast.error(extractErrorDescription(error, translation));
                    } finally {
                      dispatch(setIsRefetchTokenLoading(false));
                    }
                    setStepsAdd(Add_Company_Related_steps.ADD_0);
                  }}
                />
              )}
              {stepsAdd === Add_Company_Related_steps.ADD_2 && (
                <AddOrEditRepresentative
                  natureMandates={natureMandates}
                  mandateServices={mandateServices}
                  isPendingNatureMandates={isPendingNatureMandates}
                  agent={false}
                  onDownload={downloadDocument}
                  loading={isAddOrEditRepresentativePending}
                  CSPCodes={CSPCodes}
                  activitiesCsp={activities}
                  countries={countries?.countries ?? []}
                  documentUploaded={selectedDocument ?? undefined}
                  onValidate={async (companyRepresentative) => {
                    try {
                      setIsRefetchTokenLoading(true);

                      companyRepresentative.mandatoryInformation.linkType =
                        OlkyPassProLinkType.BENEFICIAL_OWNER;
                      const data: CompanyRelatedPersonDtoRequest = {
                        companyRelatedPersonDto: companyRepresentative,
                        companyUuid: leUuid
                      };
                      const resp = await addAgentOrBo(data);
                      if (resp.success) {
                        if (
                          data.companyRelatedPersonDto.naturalPerson?.identityJustification?.name &&
                          data.companyRelatedPersonDto.naturalPerson?.identityJustification
                            .base64docs
                        ) {
                          try {
                            await refetchDocType();
                            const docType = allCategories?.find((doc) => doc.type_en === "ID card");
                            await uploadDocument({
                              ownerUUID: resp.data.linkUuid,
                              document_type_id: docType?.id,
                              isAgentLe: true,
                              language: getLanguage(),
                              document: {
                                base64Doc:
                                  data.companyRelatedPersonDto.naturalPerson?.identityJustification
                                    ?.base64docs,
                                fileFormat: data.companyRelatedPersonDto.naturalPerson
                                  ?.identityJustification?.fileFormat as string,
                                fileName:
                                  data.companyRelatedPersonDto.naturalPerson?.identityJustification
                                    ?.name
                              }
                            });
                            setSelectedDocument(null);
                          } catch (_) {
                            toast.error(t("aliasDocumentsPage.error_import"));
                          }
                        }
                        const linkRealUuid = linksQuery?.find(
                          (link) => link.uuid === companyRepresentative.linkUuid
                        );
                        if (linkRealUuid?.realIdentity.uuid === user?.naturalPerson.UUID) {
                          await refreshToken();
                        }
                        await refetchLinks();
                        setAddBeneficiairy(false);
                      }
                    } catch (error) {
                      toast.error(extractErrorDescription(error, translation));
                      setAddBeneficiairy(false);
                      setIsRefetchTokenLoading(false);
                    }
                    setStepsAdd(Add_Company_Related_steps.ADD_0);
                  }}
                />
              )}
            </Modal>
          )}
        </>

        {leUuid && companyDetails?.legalEntity?.IsWaitingConsent && (
          <Alert
            title={t("business_links.consentdocument.sign.alert_title")}
            className="mb-4"
            type={AlertType.Warning}
          >
            <InfoAlert className="mt-2 block h-full text-alert_info-text text-warning">
              {t("business_links.consentdocument.sign.alert")}
            </InfoAlert>
          </Alert>
        )}

        {companyDetails && !isAdmin && !!companyDetails.legalEntity.UserValidation && (
          //      companyDetails.legalEntity.is_waiting_consent &&
          <div>
            {isPendingDoc ? (
              <Loader />
            ) : (
              !!consentDocument && (
                <Alert
                  className="mt-2 px-4 py-6"
                  type={AlertType.Warning}
                  title={t("business_links.consentdocument.title")}
                >
                  <Button
                    size="sm"
                    className="w-max text-sm"
                    theme={Themes.declarative}
                    onClick={() => setShowConsentDocument(true)}
                    disabled={
                      !!user?.olkyPassLevel.olkypass_status.level &&
                      user.olkyPassLevel.olkypass_status.level < 2
                    }
                    dataCy="open-consent-pro"
                  >
                    {t("business_links.open_document_button")}
                  </Button>
                </Alert>
              )
            )}
          </div>
        )}

        {isAdmin && isPendingAdminDetails ? (
          <Loader />
        ) : (
          !companyDetailsAdmin?.legalEntity?.AgentReviewFinish &&
          isAdmin && (
            <Alert
              className="px-4 py-6"
              type={AlertType.Info}
              title={t("business_links.alert.admin.title")}
            >
              <div className="">
                <InfoAlert className="mt-2 block h-full text-alert_info-text">
                  {t("business_links.alert.admin.description")}
                </InfoAlert>
              </div>
              <Button
                size="md"
                className="mt-3 w-42 md:w-80 lg:mt-5"
                dataCy="admin-validate-company-button"
                theme={Themes.white}
                loading={loadingCompanyComplete}
                onClick={() => {
                  handleCompanyAddComplete();
                }}
                iconPosition="after"
              >
                {t("business_links.alert.admin.btn_text")}
              </Button>
            </Alert>
          )
        )}

        <CompanyOrganizationalChart actionEnabled={actionEnabled} companyDetails={companyDetails} />

        <div className="mt-12">
          <ListCompanyRepresentative
            isPendingCompaniesQuery={isPendingCompaniesQuery}
            actionEnabled={actionEnabled}
            openInvite={async (link: RepresentativeItem) => {
              setModalInviteInfos({
                isOpen: true,
                link: link
              });
            }}
            openInviteCreate={(link: RepresentativeItem) => {
              setModalInviteCreateOlkyPassInfos({
                isOpen: true,
                link: link
              });
            }}
            companyStatus={
              isAdmin
                ? companyDetailsAdmin?.legalEntity?.AgentReviewFinish
                : companyDetails?.legalEntity?.UserValidation
            }
            isAdminCompanyStatus={isAdmin && !isPendingAdminDetails}
            openEdit={hanleOpenEditModal}
            openDuplicate={handleOpenDulicate}
            openDelete={onDeleteOpen}
            type={OlkyPassProLinkType.AGENT}
            representativeList={agentsList}
          />
        </div>
        {(isPendingAdminDetails && isAdmin) || (isPendingDetails && !isAdmin) ? (
          <div className="mt-5 flex w-full flex-row justify-center">
            <Loader />
          </div>
        ) : (
          ((!companyDetails?.legalEntity?.UserValidation &&
            !companyDetails?.legalEntity?.AgentReviewFinish &&
            !isAdmin) ||
            isAdmin) && (
            <div className="flex w-full flex-row justify-end">
              <Button
                size="md"
                className="mt-5 w-62 lg:w-max"
                theme={Themes.primary}
                onClick={() => {
                  setAddAgent(true);
                }}
                iconPosition="after"
                icon={<PlusCircleIcon />}
                disabled={!actionEnabled}
              >
                {t("business_links.button_1")}
              </Button>
            </div>
          )
        )}
        <div className="mt-10">
          <ListCompanyRepresentative
            companyStatus={companyDetails?.legalEntity?.UserValidation}
            actionEnabled={actionEnabled}
            isAdminCompanyStatus={isAdmin && !isPendingAdminDetails}
            isPendingCompaniesQuery={isPendingCompaniesQuery}
            openInvite={async (link: RepresentativeItem) => {
              setModalInviteInfos({
                isOpen: true,
                link: link
              });
            }}
            openInviteCreate={(link: RepresentativeItem) => {
              setModalInviteCreateOlkyPassInfos({
                isOpen: true,
                link: link
              });
            }}
            openEdit={hanleOpenEditModal}
            openDelete={onDeleteOpen}
            type={OlkyPassProLinkType.BENEFICIAL_OWNER}
            representativeList={beneficialOwnersList}
          />
        </div>
        {(isPendingAdminDetails && isAdmin) || (isPendingDetails && !isAdmin) ? (
          <div className="mt-5 flex w-full flex-row justify-center">
            <Loader />
          </div>
        ) : (
          ((!companyDetails?.legalEntity?.UserValidation &&
            !companyDetails?.legalEntity?.AgentReviewFinish &&
            !isAdmin) ||
            isAdmin) && (
            <div className="flex w-full flex-row justify-end">
              <Button
                className="mt-5 w-62 lg:w-max"
                size="md"
                theme={Themes.primary}
                onClick={() => setAddBeneficiairy(true)}
                iconPosition="after"
                disabled={!actionEnabled}
                icon={<PlusCircleIcon />}
              >
                {t("business_links.button_2")}
              </Button>
            </div>
          )
        )}
      </div>
      {modalInviteInfos && (
        <Modal
          className="max-w-5xl"
          open={modalInviteInfos.isOpen}
          onClose={() => setModalInviteInfos(null)}
        >
          <SendRepresentativeInvitation
            infos={modalInviteInfos}
            loading={isSendInvitePending}
            handleSendInvite={handleSendInvite}
          />
        </Modal>
      )}
      {modalInviteCreateOlkyPassInfos && (
        <Modal
          className="max-w-5xl"
          open={modalInviteCreateOlkyPassInfos.isOpen}
          onClose={() => setModalInviteCreateOlkyPassInfos(null)}
        >
          <SendRepresentativeCreateOlkyPassInvitation
            infos={modalInviteCreateOlkyPassInfos}
            loading={isSendInvitePending}
            handleSendInvite={handleSendInvite}
          />
        </Modal>
      )}
    </div>
  );
};
